import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import Icon, { IconName } from './Icon/Icon';
import { sanitizeForm } from 'utils/htmlSanitizer';

/**
 * Props for the ExpandableTooltip component.
 *
 * @property {IconName} [icon] - Optional icon to display within the tooltip.
 * @property {number} [iconSize] - Optional size for the icon.
 * @property {string} introText - The introductory text displayed before the expand link.
 * @property {ReactNode} [expandedContent] - Content to be displayed when the tooltip is expanded (JSX/ReactNode).
 * @property {string} [expandedContentHtml] - HTML string to be displayed when expanded (alternative to expandedContent).
 * @property {string} [className] - Optional CSS class for the tooltip container.
 * @property {string} [iconClassName] - Optional CSS class for the icon element.
 * @property {string} [expandText] - Custom text for the expand action (default: "Expand for more.")
 * @property {string} [collapseText] - Custom text for the collapse action (default: "Hide Info")
 */
type ExpandableTooltipProps = {
    icon?: IconName;
    iconSize?: number;
    introText: string;
    expandedContent?: ReactNode;
    expandedContentHtml?: string;
    className?: string;
    iconClassName?: string;
    expandText?: string;
    collapseText?: string;
};

/**
 * ExpandableTooltip component displays an icon, introductory text, and collapsible content.
 *
 * @remarks
 * - This component maintains its own expanded/collapsed state independently of parent re-renders.
 * - The expand/collapse toggle is a clickable text link that switches between two states.
 * - Unlike the legacy Tooltip component, this doesn't manipulate the DOM directly.
 * - The component is resistant to form validation re-renders and user input events.
 * - Supports both ReactNode (expandedContent) and HTML string (expandedContentHtml) for content.
 *
 * Key improvements over the legacy Tooltip:
 * - Uses React state instead of DOM manipulation
 * - Prevents unwanted collapse during typing or validation
 * - More predictable behavior with proper React patterns
 * - Better performance with memoization
 *
 * @param {ExpandableTooltipProps} props - Props for the ExpandableTooltip component.
 * @param {IconName} [props.icon='info'] - Name of the icon to display.
 * @param {number} [props.iconSize=24] - Size of the icon.
 * @param {string} props.introText - The introductory text displayed before the expand link.
 * @param {ReactNode} [props.expandedContent] - Content to display when expanded (JSX/ReactNode).
 * @param {string} [props.expandedContentHtml] - HTML string to display when expanded (takes precedence over expandedContent).
 * @param {string} [props.className=''] - Additional class names for the tooltip container.
 * @param {string} [props.iconClassName=''] - Additional class names for the icon.
 * @param {string} [props.expandText='Expand for more.'] - Custom expand text.
 * @param {string} [props.collapseText='Hide Info'] - Custom collapse text.
 *
 * @example
 * ```tsx
 * // With JSX content
 * <ExpandableTooltip
 *   introText="Need further information on how to respond?"
 *   expandedContent={
 *     <div>
 *       <p>If helpful, you can respond to the questions below:</p>
 *       <ul>
 *         <li>Question 1</li>
 *         <li>Question 2</li>
 *       </ul>
 *     </div>
 *   }
 * />
 *
 * // With HTML string
 * <ExpandableTooltip
 *   introText="Need further information?"
 *   expandedContentHtml="<div><p>Question 1</p><ul><li>Item 1</li></ul></div>"
 * />
 * ```
 */
const ExpandableTooltip: React.FC<ExpandableTooltipProps> = ({
    icon = 'info',
    iconSize = 24,
    introText,
    expandedContent,
    expandedContentHtml,
    className = '',
    iconClassName = '',
    expandText = 'Expand for more.',
    collapseText = 'Hide Info',
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    /**
     * Toggle the expanded state.
     * Uses useCallback to prevent unnecessary re-renders.
     */
    const handleToggle = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsExpanded(prev => !prev);
    }, []);

    /**
     * Memoize the toggle text to prevent unnecessary recalculations.
     */
    const toggleText = useMemo(() => {
        return isExpanded ? collapseText : expandText;
    }, [isExpanded, collapseText, expandText]);

    return (
        <div className={`d-flex align-items-start gap-6px ${className}`}>
            {icon && (
                <Icon name={icon} size={iconSize} className={`min-w-24px ${iconClassName}`} />
            )}
            <div className="fs-16px text-black w-100">
                {/* Intro text */}
                {introText}{' '}

                {/* Toggle link */}
                <button
                    type="button"
                    className="border-bottom border-black text-decoration-none fw-bold text-black cursor-pointer bg-transparent border-0 p-0"
                    onClick={handleToggle}
                >
                    {toggleText}
                </button>

                {/* Expanded content */}
                {isExpanded && (
                    <div className="bg-light-blue py-10px px-15px radius-5px mt-10px negative-ml-30px">
                        {expandedContentHtml ? (
                            <div dangerouslySetInnerHTML={{ __html: sanitizeForm(expandedContentHtml) }} />
                        ) : (
                            expandedContent
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExpandableTooltip;
